import React, { ReactElement } from 'react'
import { Box, Typography, Paper } from '@mui/material'
import {
  BarChart as GraphIcon,
  Remove as NoDataIcon,
} from '@mui/icons-material'
import JsonView from '@uiw/react-json-view'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import type { MotherSessionDetail } from '../types'

dayjs.extend(duration)

type OverviewTabProps = {
  sessionDetail: MotherSessionDetail
}

export default ({ sessionDetail }: OverviewTabProps): ReactElement => {
  let { metadata, finalResponse, progress } = sessionDetail

  // Calculate session duration from first to last progress event
  let durationFormatted = (() => {
    if (progress.length > 1) {
      let firstEvent = progress[0]
      let lastEvent = progress[progress.length - 1]
      let sessionDuration = dayjs(lastEvent.timestamp).diff(
        dayjs(firstEvent.timestamp),
      )

      // Show more precise duration formatting
      let durationObj = dayjs.duration(sessionDuration)
      if (durationObj.asMinutes() >= 1) {
        return `${Math.floor(durationObj.asMinutes())}m ${durationObj.seconds()}s`
      }
      return `${Math.floor(durationObj.asSeconds())}s`
    }
    if (progress.length === 1) {
      return 'Single event'
    }
    return 'No progress data'
  })()

  // Get latest progress event
  let latestProgress =
    progress.length > 0 ? progress[progress.length - 1] : null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Session Metadata */}
      <Paper sx={{ p: 3, bgcolor: 'background.paperDark' }}>
        <Typography variant="h6" gutterBottom>
          Session Information
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Correlation ID
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
            >
              {metadata.correlationID}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              User ID
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
            >
              {metadata.userID}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Status
            </Typography>
            <Typography variant="body1">
              {metadata.status.replace('Session', '')}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Duration
            </Typography>
            <Typography variant="body1">{durationFormatted}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Created
            </Typography>
            <Typography variant="body1">
              {dayjs(metadata.createdTime).format('MMM D, YYYY h:mm:ss A')}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Last Updated
            </Typography>
            <Typography variant="body1">
              {dayjs(metadata.updatedTime).format('MMM D, YYYY h:mm:ss A')}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Session Progress Summary */}
      <Paper sx={{ p: 3, bgcolor: 'background.paperDark' }}>
        <Typography variant="h6" gutterBottom>
          Progress Summary
        </Typography>
        <Box
          sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h4"
              color="primary"
              aria-label={`${progress.length} progress events`}
            >
              {progress.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Progress Events
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h4"
              color={
                metadata.hasExecutionGraph ? 'success.main' : 'text.disabled'
              }
            >
              {metadata.hasExecutionGraph ? (
                <GraphIcon aria-label="Execution graph available" />
              ) : (
                <NoDataIcon color="disabled" aria-label="No execution graph" />
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Execution Graph
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h4"
              sx={{ color: 'info.main' }}
              aria-label={`${metadata.loreCount} lore files`}
            >
              {metadata.loreCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Lore Files
            </Typography>
          </Box>
        </Box>

        {latestProgress && (
          <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Latest Progress
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
              {latestProgress.phase}: {latestProgress.message}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {dayjs(latestProgress.timestamp).format('MMM D, YYYY h:mm:ss A')}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Final Response */}
      {finalResponse && (
        <Paper sx={{ p: 3, bgcolor: 'background.paperDark' }}>
          <Typography variant="h6" gutterBottom>
            Final Response
          </Typography>
          <Box
            sx={{
              bgcolor: 'background.paper',
              p: 2,
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
              maxHeight: 400,
              overflowY: 'auto',
            }}
          >
            <JsonView
              value={(() => {
                try {
                  return JSON.parse(finalResponse)
                } catch {
                  return { rawResponse: finalResponse }
                }
              })()}
              style={
                {
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  fontSize: '14px',
                  '--w-rjv-color': '#ffffff',
                  '--w-rjv-key-string': '#9db7ff',
                  '--w-rjv-background-color': 'transparent',
                } as any
              }
              displayDataTypes={false}
              displayObjectSize={false}
              enableClipboard={true}
            />
          </Box>
        </Paper>
      )}
    </Box>
  )
}
